import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sensorsApi } from '../services/api';
import {
  ArrowLeftIcon,
  Cog6ToothIcon,
  TrashIcon,
  SignalIcon,
  SignalSlashIcon,
  WrenchIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  ClockIcon,
  ServerStackIcon,
  HeartIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  CommandLineIcon,
  WifiIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/common/ConfirmModal';
import ConfigureSensorModal from '../components/sensors/ConfigureSensorModal';

// ============================================================================
// Constants
// ============================================================================

const statusConfig: Record<string, { icon: any; color: string; bgColor: string; label: string }> = {
  ONLINE: { icon: SignalIcon, color: 'text-accent', bgColor: 'bg-accent/10', label: 'Online' },
  OFFLINE: { icon: SignalSlashIcon, color: 'text-gray-400', bgColor: 'bg-gray-500/10', label: 'Offline' },
  MAINTENANCE: { icon: WrenchIcon, color: 'text-yellow-400', bgColor: 'bg-yellow-500/10', label: 'Maintenance' },
  ERROR: { icon: ExclamationTriangleIcon, color: 'text-alert', bgColor: 'bg-alert/10', label: 'Error' },
  DEGRADED: { icon: ExclamationTriangleIcon, color: 'text-yellow-400', bgColor: 'bg-yellow-500/10', label: 'Degraded' },
};

const sensorTypeLabels: Record<string, string> = {
  RADAR: 'Radar',
  RF_DETECTOR: 'RF Detector',
  OPTICAL: 'Optical',
  ADS_B: 'ADS-B',
  ACOUSTIC: 'Acoustic',
  MULTI_SENSOR: 'Multi-Sensor Fusion',
  MANUAL: 'Manual Entry',
};

const dataFormatLabels: Record<string, string> = {
  SAPIENT: 'SAPIENT',
  COT: 'Cursor on Target (CoT)',
  ADS_B: 'ADS-B',
  DUMP: 'DUMP (Dronetag)',
  JSON_CUSTOM: 'Custom JSON',
};

const tabs = [
  { id: 'overview', label: 'Overview', icon: InformationCircleIcon },
  { id: 'ingestion', label: 'Ingestion Setup', icon: CommandLineIcon },
  { id: 'health', label: 'Health & Diagnostics', icon: HeartIcon },
] as const;

type TabId = (typeof tabs)[number]['id'];

// ============================================================================
// Copy Button helper
// ============================================================================

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button onClick={copy} className="flex items-center gap-1.5 text-gray-500 hover:text-accent transition-colors" title={label || 'Copy'}>
      {copied ? <CheckIcon className="h-4 w-4 text-accent" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
      {label && <span className="text-xs">{copied ? 'Copied' : label}</span>}
    </button>
  );
}

// ============================================================================
// Overview Tab
// ============================================================================

function OverviewTab({ sensor }: { sensor: any }) {
  const config = statusConfig[sensor.status] || statusConfig.OFFLINE;
  const StatusIcon = config.icon;

  const getTimeSince = (dateStr: string) => {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Status + basic info */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Status</p>
          <div className="flex items-center gap-2">
            <StatusIcon className={clsx('h-5 w-5', config.color)} />
            <span className={clsx('text-lg font-display font-bold', config.color)}>{config.label}</span>
          </div>
        </div>
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Sensor Type</p>
          <p className="text-lg font-display font-bold text-white">{sensorTypeLabels[sensor.sensorType] || sensor.sensorType}</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Data Format</p>
          <p className="text-lg font-display font-bold text-white">{dataFormatLabels[sensor.dataFormat] || sensor.dataFormat}</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Tracks (24h)</p>
          <p className="text-lg font-display font-bold text-accent">{sensor.recentTracks24h ?? '—'}</p>
        </div>
      </div>

      {/* Location */}
      <div className="glass-card p-6">
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <MapPinIcon className="h-5 w-5 text-gray-400" /> Location
        </h3>
        {sensor.latitude != null && sensor.longitude != null ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Latitude</p>
              <p className="text-sm font-mono text-white">{sensor.latitude.toFixed(6)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Longitude</p>
              <p className="text-sm font-mono text-white">{sensor.longitude.toFixed(6)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Altitude (MSL)</p>
              <p className="text-sm font-mono text-white">{sensor.altitudeMsl != null ? `${sensor.altitudeMsl}m` : '—'}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No location configured</p>
        )}
      </div>

      {/* Last heartbeat + config */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="glass-card p-6">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-gray-400" /> Activity
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Last Heartbeat</p>
              <p className="text-sm text-white">{sensor.lastHeartbeat ? getTimeSince(sensor.lastHeartbeat) : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Last Seen</p>
              <p className="text-sm text-white">{sensor.lastSeen ? getTimeSince(sensor.lastSeen) : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Created</p>
              <p className="text-sm text-white">{new Date(sensor.createdAt).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        <div className="glass-card p-6">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Cog6ToothIcon className="h-5 w-5 text-gray-400" /> Configuration
          </h3>
          {sensor.configuration ? (
            <div className="grid grid-cols-2 gap-3">
              {sensor.configuration.updateRateHz != null && (
                <div>
                  <p className="text-xs text-gray-500">Update Rate</p>
                  <p className="text-sm text-white">{sensor.configuration.updateRateHz} Hz</p>
                </div>
              )}
              {sensor.configuration.maxRange != null && (
                <div>
                  <p className="text-xs text-gray-500">Max Range</p>
                  <p className="text-sm text-white">{(sensor.configuration.maxRange / 1000).toFixed(1)} km</p>
                </div>
              )}
              {sensor.configuration.minRange != null && (
                <div>
                  <p className="text-xs text-gray-500">Min Range</p>
                  <p className="text-sm text-white">{sensor.configuration.minRange}m</p>
                </div>
              )}
              {sensor.configuration.fieldOfView != null && (
                <div>
                  <p className="text-xs text-gray-500">Field of View</p>
                  <p className="text-sm text-white">{sensor.configuration.fieldOfView}&deg;</p>
                </div>
              )}
              {!sensor.configuration.updateRateHz && !sensor.configuration.maxRange && !sensor.configuration.minRange && !sensor.configuration.fieldOfView && (
                <p className="text-sm text-gray-500 col-span-2">No configuration set</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No configuration set</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Ingestion Setup Tab
// ============================================================================

function IngestionTab({ sensor }: { sensor: any }) {
  // Use the dedicated ingestion service URL from env, NOT the web frontend origin
  const ingestionHost = import.meta.env.VITE_INGESTION_URL || 'http://localhost:4100';

  const httpEndpoint = `${ingestionHost}/ingest`;
  const dumpEndpoint = `${ingestionHost}/ingest/dump`;
  const wsEndpoint = `${ingestionHost.replace('http', 'ws')}/ws/ingest`;

  const isDump = sensor.dataFormat === 'DUMP';

  return (
    <div className="space-y-6">
      {/* Dronetag Scout — streamlined setup shown first for DUMP sensors */}
      {isDump && (
        <div className="glass-card p-6 glow-border">
          <h3 className="text-base font-semibold text-white mb-1 flex items-center gap-2">
            <WifiIcon className="h-5 w-5 text-accent" /> Dronetag Scout Setup
          </h3>
          <p className="text-xs text-gray-500 mb-5">Connect your Dronetag Scout to ASNE C2 in 3 steps via the Dronetag Integration Portal.</p>

          <div className="space-y-5">
            {/* Step 1 */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/10 text-accent text-xs flex items-center justify-center font-bold">1</span>
                <p className="text-sm font-medium text-white">Open the Dronetag Integration Portal</p>
              </div>
              <p className="text-xs text-gray-400 ml-8 mb-2">
                Go to <a href="https://integrations.dronetag.app" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">integrations.dronetag.app</a> and
                create a new <span className="text-white font-medium">HTTP distribution</span> (or TCP Socket) for your Scout device.
              </p>
              <div className="ml-8 flex items-start gap-2 p-2.5 bg-sky/10 border border-sky/20 rounded-lg">
                <InformationCircleIcon className="h-4 w-4 text-sky flex-shrink-0 mt-0.5" />
                <p className="text-xs text-sky/90">
                  Requires a <span className="font-semibold text-white">Dronetag Pro plan</span>. Distribution is configured in the portal, not on the Scout device.
                </p>
              </div>
            </div>

            {/* Step 2 — Target URL */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/10 text-accent text-xs flex items-center justify-center font-bold">2</span>
                <p className="text-sm font-medium text-white">Set the target URL</p>
              </div>
              <p className="text-xs text-gray-400 ml-8 mb-2">Copy this URL and paste it as the <span className="text-white">target URL</span> in the portal:</p>
              <div className="ml-8 p-3 bg-navy-900 rounded-lg border border-accent/30">
                <div className="flex items-center justify-between">
                  <code className="text-sm text-accent font-mono break-all">{dumpEndpoint}</code>
                  <CopyButton value={dumpEndpoint} label="Copy" />
                </div>
              </div>
            </div>

            {/* Step 3 — Headers */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/10 text-accent text-xs flex items-center justify-center font-bold">3</span>
                <p className="text-sm font-medium text-white">Add custom headers</p>
              </div>
              <p className="text-xs text-gray-400 ml-8 mb-2">Add each of these as <code className="text-gray-300 bg-navy-700 px-1 py-0.5 rounded">target_additional_http_headers</code> in the portal:</p>
              <div className="ml-8 p-3 bg-navy-900 rounded-lg border border-navy-700 space-y-2">
                {[
                  { header: 'x-sensor-id', value: sensor.id },
                  { header: 'x-tenant-id', value: sensor.tenantId },
                  { header: 'x-data-format', value: 'DUMP' },
                  { header: 'x-sensor-type', value: sensor.sensorType },
                ].map((h) => (
                  <div key={h.header} className="flex items-center justify-between">
                    <code className="text-xs text-accent font-mono">{h.header}: {h.value}</code>
                    <CopyButton value={`${h.header}: ${h.value}`} />
                  </div>
                ))}
              </div>
            </div>

            {/* Done — verification */}
            <div className="ml-8 mt-2 p-3 bg-accent/5 border border-accent/20 rounded-lg">
              <p className="text-xs text-gray-400">
                Once enabled, Dronetag will push Remote ID telemetry to ASNE C2. Tracks should appear on the <span className="text-white">Live Map</span> within seconds. Check the <span className="text-white">Health &amp; Diagnostics</span> tab to verify data flow.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Ingestion Endpoint — single HTTP endpoint for DUMP, all options for other formats */}
      <div className="glass-card p-6">
        <h3 className="text-base font-semibold text-white mb-1">{isDump ? 'Ingestion Endpoint' : 'Ingestion Endpoints'}</h3>
        <p className="text-xs text-gray-500 mb-5">
          {isDump
            ? 'Your Dronetag Scout sends data to this HTTP endpoint.'
            : 'Use these endpoints to send data from your sensor to ASNE C2.'}
        </p>

        <div className="space-y-4">
          <div className="p-3 bg-navy-900 rounded-lg border border-navy-700">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-400">HTTP (REST)</span>
              <CopyButton value={isDump ? dumpEndpoint : httpEndpoint} label="Copy" />
            </div>
            <code className="text-sm text-accent font-mono break-all">{isDump ? dumpEndpoint : httpEndpoint}</code>
          </div>

          {/* Only show WebSocket and UDP for non-Dronetag sensors */}
          {!isDump && (
            <>
              <div className="p-3 bg-navy-900 rounded-lg border border-navy-700">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-400">WebSocket</span>
                  <CopyButton value={wsEndpoint} label="Copy" />
                </div>
                <code className="text-sm text-accent font-mono break-all">{wsEndpoint}</code>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Required Headers */}
      <div className="glass-card p-6">
        <h3 className="text-base font-semibold text-white mb-1">Required Headers</h3>
        <p className="text-xs text-gray-500 mb-5">Include these headers with every HTTP request.</p>

        <div className="space-y-3">
          {[
            { header: 'x-sensor-id', value: sensor.id },
            { header: 'x-tenant-id', value: sensor.tenantId },
            { header: 'x-data-format', value: sensor.dataFormat },
            { header: 'x-sensor-type', value: sensor.sensorType },
          ].map((h) => (
            <div key={h.header} className="flex items-center justify-between p-3 bg-navy-900 rounded-lg border border-navy-700">
              <div>
                <code className="text-xs text-gray-400 font-mono">{h.header}</code>
                <code className="text-sm text-white font-mono ml-3">{h.value}</code>
              </div>
              <CopyButton value={h.value} />
            </div>
          ))}
        </div>
      </div>

      {/* API Key */}
      {sensor.ingestionKeyPrefix && (
        <div className="glass-card p-6">
          <h3 className="text-base font-semibold text-white mb-1">Sensor API Key</h3>
          <p className="text-xs text-gray-500 mb-4">The full key was shown once at registration. Only the prefix is stored.</p>
          <div className="p-3 bg-navy-900 rounded-lg border border-navy-700">
            <code className="text-sm text-gray-400 font-mono">{sensor.ingestionKeyPrefix}••••••••</code>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Health & Diagnostics Tab
// ============================================================================

function HealthTab({ sensor }: { sensor: any }) {
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  const runTest = async () => {
    setTesting(true);
    try {
      const result = await sensorsApi.test(sensor.id);
      setTestResult(result.data || result);
      toast.success('Connection test completed');
    } catch {
      toast.error('Connection test failed');
      setTestResult({ error: 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const getTimeSince = (dateStr: string) => {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Test Connection */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-white">Connection Test</h3>
            <p className="text-xs text-gray-500">Test the sensor's connectivity and data flow.</p>
          </div>
          <button onClick={runTest} disabled={testing} className="btn-primary flex items-center gap-2 text-sm">
            <ArrowPathIcon className={clsx('h-4 w-4', testing && 'animate-spin')} />
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
        </div>

        {testResult && !testResult.error && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            {testResult.connectivity && (
              <div className="p-3 bg-navy-900 rounded-lg border border-navy-700">
                <p className="text-xs text-gray-500 mb-1">Connectivity</p>
                <p className={clsx('text-sm font-medium', testResult.connectivity.heartbeatAgeMs < 120_000 ? 'text-accent' : 'text-alert')}>
                  {testResult.connectivity.heartbeatAgeMs < 120_000 ? 'Connected' : 'No Recent Heartbeat'}
                </p>
                {testResult.connectivity.lastHeartbeat && (
                  <p className="text-xs text-gray-500 mt-1">Last: {getTimeSince(testResult.connectivity.lastHeartbeat)}</p>
                )}
              </div>
            )}
            {testResult.dataFlow && (
              <div className="p-3 bg-navy-900 rounded-lg border border-navy-700">
                <p className="text-xs text-gray-500 mb-1">Data Flow (1h)</p>
                <p className="text-sm font-medium text-white">{testResult.dataFlow.tracksLastHour ?? 0} tracks</p>
                {testResult.dataFlow.latestTrack && (
                  <p className="text-xs text-gray-500 mt-1">Latest: {getTimeSince(testResult.dataFlow.latestTrack)}</p>
                )}
              </div>
            )}
            {testResult.diagnostics && (
              <div className="p-3 bg-navy-900 rounded-lg border border-navy-700">
                <p className="text-xs text-gray-500 mb-1">Diagnostics</p>
                {testResult.diagnostics.firmwareVersion && (
                  <p className="text-sm text-white">FW: {testResult.diagnostics.firmwareVersion}</p>
                )}
                {testResult.diagnostics.ipAddress && (
                  <p className="text-xs text-gray-500 mt-1">{testResult.diagnostics.ipAddress}</p>
                )}
              </div>
            )}
          </div>
        )}

        {testResult?.error && (
          <div className="mt-4 p-3 bg-alert/10 border border-alert/20 rounded-lg">
            <p className="text-sm text-alert">{testResult.error}</p>
          </div>
        )}
      </div>

      {/* Sensor Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="glass-card p-6">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <ServerStackIcon className="h-5 w-5 text-gray-400" /> System Info
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Sensor ID</p>
              <div className="flex items-center gap-2">
                <code className="text-sm text-white font-mono">{sensor.id}</code>
                <CopyButton value={sensor.id} />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500">Tenant ID</p>
              <div className="flex items-center gap-2">
                <code className="text-sm text-white font-mono">{sensor.tenantId}</code>
                <CopyButton value={sensor.tenantId} />
              </div>
            </div>
            {sensor.firmwareVersion && (
              <div>
                <p className="text-xs text-gray-500">Firmware Version</p>
                <p className="text-sm text-white">{sensor.firmwareVersion}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-500">Created</p>
              <p className="text-sm text-white">{new Date(sensor.createdAt).toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="glass-card p-6">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <HeartIcon className="h-5 w-5 text-gray-400" /> Heartbeat
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Last Heartbeat</p>
              <p className="text-sm text-white">{sensor.lastHeartbeat ? getTimeSince(sensor.lastHeartbeat) : 'Never'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Last Seen</p>
              <p className="text-sm text-white">{sensor.lastSeen ? getTimeSince(sensor.lastSeen) : 'Never'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Last Updated</p>
              <p className="text-sm text-white">{sensor.updatedAt ? new Date(sensor.updatedAt).toLocaleString() : '—'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function SensorDetailPage() {
  const { sensorId } = useParams<{ sensorId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showConfigure, setShowConfigure] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const { data: sensorData, isLoading, error } = useQuery({
    queryKey: ['sensor', sensorId],
    queryFn: () => sensorsApi.get(sensorId!),
    enabled: !!sensorId,
  });

  const deleteMutation = useMutation({
    mutationFn: () => sensorsApi.delete(sensorId!),
    onSuccess: () => {
      toast.success('Sensor deleted');
      queryClient.invalidateQueries({ queryKey: ['sensors'] });
      navigate('/sensors');
    },
    onError: () => toast.error('Failed to delete sensor'),
  });

  const sensor = sensorData?.data;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-navy-900 p-6 grid-overlay">
        <div className="space-y-6">
          <div className="h-8 w-48 skeleton rounded-lg" />
          <div className="h-12 w-full skeleton rounded-lg" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 skeleton rounded-lg" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error || !sensor) {
    return (
      <div className="min-h-screen bg-navy-900 p-6 grid-overlay flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-gray-600 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">Sensor Not Found</h2>
          <p className="text-sm text-gray-500 mb-6">The sensor you're looking for doesn't exist or has been removed.</p>
          <Link to="/sensors" className="btn-primary text-sm">Back to Sensors</Link>
        </div>
      </div>
    );
  }

  const statusCfg = statusConfig[sensor.status] || statusConfig.OFFLINE;
  const StatusIcon = statusCfg.icon;

  return (
    <div className="min-h-screen bg-navy-900 p-6 grid-overlay">
      {/* Header */}
      <div className="mb-6">
        <Link to="/sensors" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-4">
          <ArrowLeftIcon className="h-4 w-4" /> Back to Sensors
        </Link>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={clsx('p-3 rounded-xl', statusCfg.bgColor, statusCfg.color)}>
              <StatusIcon className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold text-white">{sensor.name}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm text-gray-500">{sensorTypeLabels[sensor.sensorType] || sensor.sensorType}</span>
                <span className="text-gray-700">&middot;</span>
                <span className={clsx('text-xs px-2 py-0.5 rounded-full font-semibold', statusCfg.bgColor, statusCfg.color)}>{statusCfg.label}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowConfigure(true)} className="btn-secondary flex items-center gap-2 text-sm">
              <Cog6ToothIcon className="h-4 w-4" /> Configure
            </button>
            <button onClick={() => setShowDelete(true)} className="btn-secondary flex items-center gap-2 text-sm text-alert hover:bg-alert/10 hover:border-alert/30">
              <TrashIcon className="h-4 w-4" /> Delete
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex gap-1 bg-navy-800 rounded-lg p-1 border border-navy-700 w-fit">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={clsx('flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                  activeTab === tab.id ? 'bg-accent text-navy-900' : 'text-gray-400 hover:text-white')}>
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab sensor={sensor} />}
      {activeTab === 'ingestion' && <IngestionTab sensor={sensor} />}
      {activeTab === 'health' && <HealthTab sensor={sensor} />}

      {/* Modals */}
      <ConfigureSensorModal isOpen={showConfigure} onClose={() => setShowConfigure(false)} sensor={sensor} />
      <ConfirmModal isOpen={showDelete} onClose={() => setShowDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete Sensor" message={`Are you sure you want to delete "${sensor.name}"? This action cannot be undone. All associated data will be permanently removed.`}
        confirmLabel="Delete Sensor" variant="danger" loading={deleteMutation.isPending} />
    </div>
  );
}
